<template>
  <v-card height="90vh" id="ticket-expansion">
    <v-card-title :class="headerClass">
      <span class="headline" style="min-width:153px">
        {{ $t('expandTickets') }}
      </span>
      <v-spacer/>
      <v-row dense justify="end">
        <v-col cols="12" md="6" xl="auto" v-for="f in filterTypes.map((ft) => ({ key: ft.key, val: selectedFilters[ft.key] }))" :key="f.key">
          <div>
            <v-select
              :prepend-inner-icon="IconHelpers.getIconForObjectType(f.key)"
              clearable
              :items="filterItems[f.key]"
              :value="filterItems[f.key]?.length === 1 ? filterItems[f.key][0] : f.val"
              return-object
              @input="setFilter(f.key, $event)"
              outlined
              rounded
              dense
              dark
              color="white"
              :label="$t(f.key)"
              class="mb-n6 mr-2"
              style="min-width:260px"
              :id="`ticketExpansionFilter${$t(f.key)}`"
            >
              <template #item="{ item, on }">
                <v-list-item v-on="on">
                  <span>{{ item }}</span>
                </v-list-item>
              </template>
              <template #selection="{ item }">
                <span>{{ item }}</span>
              </template>
            </v-select>
          </div>
        </v-col>
      </v-row>
      <BaseDialogActions
        @close="$emit('close')"
        hideRefresh
        @refresh="refreshData"
      />
    </v-card-title>

    <v-card-text>
      <v-row dense>
        <v-col cols="12" md="6" xl="4">
          <v-text-field
            v-model="search"
            single-line
            color="primary"
            append-icon="mdi-magnify"
            clearable
            :label="$t('search')"
          />
        </v-col>
      </v-row>

      <v-container fluid v-show="ticketsLoader.loading" class="fill-height">
        <v-row align="center" justify="center">
          <v-progress-circular indeterminate color="primary" />
        </v-row>
      </v-container>

      <v-row v-if="!ticketsLoader.loading && noTickets" align="center" justify="center">
        <v-col cols="12">
          <NoData>{{ $t('noTicketAvailableForExpansion') }}</NoData>
        </v-col>
      </v-row>

      <v-row v-if="!ticketsLoader.loading && !noTickets">
        <v-col cols="12">
          <v-data-table
            v-model="selectedTickets"
            :items="filteredTickets"
            :search="search"
            :items-per-page="dataTableDefaultItemsPerPage"
            dense
            :headers="headers"
            :footer-props="{ 'items-per-page-options': dataTableItemsPerPage }"
            item-key="ticketId"
            show-select
            id="expand-tickets-table"
            checkbox-color="success"
            fixed-header
            @update:items-per-page="updateCurrentItemsPerPage"
          >
            <template #header.data-table-select>
              <Icon
                top
                :icon="iconForSelectAllBox"
                :iconColor="allSelected || allFilteredSelected ? 'success' : 'rgba(0,0,0,0.3)'"
                :tooltipText="selectAllTooltipText"
                tooltipColor="secondary"
                :small="false"
                @icon-clicked="selectAllChanged"
                :disabled="selectAllDisabled"
                :style="selectAllStyle"
              />
            </template>
            <template #footer.prepend>
              <v-chip :color="allSelected ? 'success' : ''">
                <span>
                  {{ `${$t('selected')}: ${selectedTickets.length} / ${tickets.length}` }}
                </span>
              </v-chip>
              <v-chip v-if="hasFilter && $vuetify.breakpoint.mdAndUp" :color="allFilteredSelected ? 'success' : ''" class="ml-1">
                <v-row no-gutters align="center">
                  <v-col v-for="(f, index) in activeFilters" :key="f[0]">
                    <v-row no-gutters align="center" class="flex-nowrap">
                      <v-col cols="auto" style="height: 22px;">
                        <v-icon x-small :color="allFilteredSelected ? 'white' : 'secondary'">{{ IconHelpers.getIconForObjectType(f[0]) }}</v-icon>
                      </v-col>

                      <v-col v-if="$vuetify.breakpoint.lgAndUp">
                        <span class="ml-1">{{ f[1] }}</span>
                        <span v-if="index < activeFilters.length - 1" class="mr-2">,</span>
                      </v-col>
                    </v-row>
                  </v-col>

                  <v-col>
                    <span>{{ `: ${intersection(selectedTickets, filteredTickets).length} / ${filteredTickets.length}` }}</span>
                  </v-col>
                </v-row>
              </v-chip>
            </template>
            <template #item.extTicketNumber1="{ item }">
              <span>{{ item.extTicketNumber1 || '-' }}</span>
            </template>
            <template #item.extTicketNumber2="{ item }">
              <span>{{ item.extTicketNumber2 || '-' }}</span>
            </template>
            <template #item.tract="{ item }">
              <span>{{ item.tract ?? $t('notAvailable') }}</span>
            </template>
            <template #item.account="{ item }">
              <span>{{ item.account ?? $t('notAvailable') }}</span>
            </template>
            <template #item.destination="{ item }">
              <span>{{ item.destination ?? $t('notAvailable') }}</span>
            </template>
            <template #item.product="{ item }">
              <span>{{ item.product ?? $t('notAvailable') }}</span>
            </template>
            <template #item.netWeight="{ item }">
              <span>{{ tonStringFromPounds(item.netWeight) }}</span>
            </template>
            <template #item.weighedOutAt="{ item }">
              <span>{{ utcToLocalDate(item.weighedOutAt, 'L - LT') }}</span>
            </template>
          </v-data-table>
        </v-col>
      </v-row>
    </v-card-text>

    <v-card-actions class="pb-4 px-0">
      <v-row dense align="center" justify="end">
        <v-col cols="auto">
          <v-btn
            id="expand-tickets-button"
            :class="headerClass"
            :disabled="selectedTickets.length === 0 || ticketsLoader.loading || expanding || !userAssignedClaim(UserClaims.TicketManager)"
            :loading="expanding"
            @click="expandSelectedTickets"
          >
            {{ $t('expandTickets') }}
          </v-btn>
        </v-col>
      </v-row>
    </v-card-actions>
  </v-card>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'
import TicketHeaders from '@/headers/Ticket'
import { ResourceLoader } from '@/utils/ResourceLoader'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { tonStringFromPounds } from '@/utils/NumericMutations.js'
import { ContractMode, ScalingStatus, UserClaims } from '@/utils/Enumerations'
import { userAssignedClaim } from '@/utils/ClaimUtility'
import { ticketFiltersForPosting } from '@/utils/TicketFiltering'
import IconHelpers from '@/utils/IconHelpers'
import { colorClassForContractMode, dataTableDefaultItemsPerPage, dataTableItemsPerPage } from '@/utils/componentHelpers'
import { isEqual, union, difference, intersection } from 'lodash'

export default {
  name: 'ExpandTickets',

  components: {
    BaseDialogActions: () => import('@/components/core/BaseDialogActions.vue'),
    Icon: () => import('@/components/helper/Icon.vue'),
    NoData: () => import('@/components/core/NoData.vue')
  },

  props: {
    contractMode: {
      type: Object,
      default: () => ({ value: ContractMode.Logs.value })
    }
  },

  data: () => ({
    IconHelpers,
    UserClaims,
    dataTableDefaultItemsPerPage,
    dataTableItemsPerPage,
    ticketsLoader: ResourceLoader.empty,
    tickets: [],
    selectedTickets: [],
    search: '',
    expanding: false,
    currentItemsPerPage: dataTableDefaultItemsPerPage,
    selectedFilters: {},
    filterItems: {}
  }),

  computed: {
    headers () {
      return this.contractMode.value === ContractMode.Logs.value
        ? TicketHeaders.logsTicketExpansionHeaders()
        : TicketHeaders.lysAndTransferTicketExpansionHeaders()
    },

    filterTypes () {
      return ticketFiltersForPosting(this.contractMode.value === ContractMode.Logs.value)
    },

    headerClass () {
      return colorClassForContractMode(this.contractMode.value)
    },

    isLogsMode () {
      return this.contractMode.value === ContractMode.Logs.value
    },

    isLogYardSaleMode () {
      return this.contractMode.value === ContractMode.LogYardSale.value
    },

    isTransferMode () {
      return this.contractMode.value === ContractMode.Transfers.value
    },

    noTickets () {
      return this.tickets.length === 0
    },

    filteredTickets () {
      return this.tickets.filter((t) => Object.keys(this.selectedFilters)
        .filter(filterKey => this.selectedFilters[filterKey])
        .every(filterKey => t[filterKey] === this.selectedFilters[filterKey]))
    },

    activeFilters () {
      return Object.entries(this.selectedFilters).filter(([, value]) => value !== undefined)
    },

    hasFilter () {
      return this.activeFilters.length > 0
    },

    allSelected () {
      if (this.selectedTickets.length === 0) return false
      return isEqual(this.selectedTickets.map(t => t.ticketId).toSorted(), this.tickets.map(t => t.ticketId).toSorted())
    },

    allFilteredSelected () {
      if (this.selectedTickets.length === 0) return false
      return this.filteredTickets.every(t => this.selectedTickets.includes(t))
    },

    iconForSelectAllBox () {
      return this.allSelected ? 'mdi-checkbox-multiple-marked'
        : this.allFilteredSelected ? 'mdi-checkbox-multiple-marked-outline'
          : this.selectedTickets.length > 0 ? 'mdi-minus-box-multiple'
            : 'mdi-checkbox-multiple-blank-outline'
    },

    selectAllTooltipText () {
      if (this.hasFilter) {
        return this.allFilteredSelected || this.allSelected
          ? this.$t('deselectAllFiltered')
          : this.$t('selectAllFiltered')
      }

      return this.allSelected
        ? this.$t('deselectAll')
        : this.$t('selectAll')
    },

    selectAllDisabled () {
      return !!this.search
    },

    selectAllStyle () {
      return this.selectAllDisabled ? 'pointer-events: none; opacity: 0.25;' : ''
    }
  },

  created () {
    this.refreshData()
  },

  methods: {
    ...mapActions('ticket', ['fetchTicketsForExpansion', 'patchTicket']),
    ...mapMutations('snackbar', ['setSnack']),
    utcToLocalDate,
    tonStringFromPounds,
    userAssignedClaim,
    intersection,

    async refreshData () {
      this.ticketsLoader = new ResourceLoader(async () => {
        this.selectedTickets = []
        this.tickets = await this.fetchTicketsForExpansion({
          isLogs: this.isLogsMode,
          isLogYardSale: this.isLogYardSaleMode,
          isTransfer: this.isTransferMode
        })
        this.setFilterItems()
      })

      await this.ticketsLoader.load()
    },

    setFilter (filterTypeKey, filterValue) {
      if (!filterValue) {
        delete this.selectedFilters[filterTypeKey]
        this.selectedFilters = JSON.parse(JSON.stringify(this.selectedFilters))
      } else {
        this.selectedFilters = { ...this.selectedFilters, [filterTypeKey]: filterValue }
      }
    },

    setFilterItems () {
      this.filterTypes.forEach(ft => {
        this.filterItems[ft.key] = [...new Set(this.tickets.map(t => t[ft.key]))].filter(i => i !== null).sort((a, b) => a.localeCompare(b))
        if (this.filterItems[ft.key]?.length < 1 || !this.filterItems[ft.key].includes(this.selectedFilters[ft.key])) {
          this.setFilter(ft.key, undefined)
        }
      })
    },

    selectAllChanged () {
      if (this.hasFilter) {
        if (this.allFilteredSelected || this.allSelected) {
          this.selectedTickets = difference(this.selectedTickets, this.filteredTickets)
        } else {
          this.selectedTickets = union(this.selectedTickets, this.filteredTickets)
        }
      } else if (this.allSelected) {
        this.selectedTickets = []
      } else {
        this.selectedTickets = [...this.tickets]
      }
    },

    updateCurrentItemsPerPage (itemsPerPage) {
      this.currentItemsPerPage = itemsPerPage
    },

    async expandSelectedTickets () {
      if (this.selectedTickets.length === 0) return

      this.expanding = true
      try {
        const patchResults = await Promise.allSettled(this.selectedTickets.map(ticket => this.patchTicket({
          ticketId: ticket.ticketId,
          modifiedAt: ticket.modifiedAt,
          patchObject: [{
            op: 'add',
            path: '/scalingStatus',
            value: ScalingStatus.Expansion.value
          }]
        })))

        const failedCount = patchResults.filter(result => result.status === 'rejected').length
        const succeededCount = patchResults.length - failedCount

        if (succeededCount > 0) {
          this.$emit('refresh')
          await this.refreshData()
        }

        if (succeededCount > 0 && failedCount > 0) {
          this.setSnack(`${this.$t('ticketsExpanded', { ticketCount: succeededCount })} ${failedCount} ticket${failedCount === 1 ? '' : 's'} failed to expand.`)
        } else if (succeededCount > 0) {
          this.setSnack(this.$t('ticketsExpanded', { ticketCount: succeededCount }))
        } else if (failedCount > 0) {
          this.setSnack(`${failedCount} ticket${failedCount === 1 ? '' : 's'} failed to expand.`)
        }
      } finally {
        this.expanding = false
      }
    }
  }
}
</script>
